import {AbstractControl, FormGroup, ValidationErrors} from '@angular/forms';
import {PixKeyType as RecipientPixKeyType} from '@gatewee/types/recipient-bank-account';
import {PixKeyType} from '@gatewee/types/banking-info';
import {isValidCNPJ, isValidCPF} from '../validators/cpf-cnpjs-validators';

export enum PixKeyErrorType {
  CPF = 'CPF',
  CNPJ = 'CNPJ',
  MAIL = 'MAIL',
  PHONE = 'PHONE',
  RANDOM_KEY = 'RANDOM_KEY',
}

function sanitizePhone(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  return cleaned.length === 11 ? `+55${cleaned}` : `+${cleaned}`;
}

const validators: Record<PixKeyErrorType, (key: string) => boolean> = {
  [PixKeyErrorType.CPF]: k => isValidCPF(k.replace(/\D/g, '')),
  [PixKeyErrorType.CNPJ]: k => isValidCNPJ(k.replace(/\D/g, '')),
  [PixKeyErrorType.MAIL]: k => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(k),
  [PixKeyErrorType.PHONE]: k => /^\+[1-9]\d{1,14}$/.test(sanitizePhone(k)),
  [PixKeyErrorType.RANDOM_KEY]: k =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      k,
    ),
};

/** Validates PIX key using API `RecipientBankAccount.pixKeyType` enum values. */
export function validateRecipientBankAccountPixKey(
  pixKey: string,
  pixKeyType: RecipientPixKeyType,
): boolean {
  const trimmed = String(pixKey ?? '').trim();
  switch (pixKeyType) {
    case RecipientPixKeyType.DOCUMENT: {
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length === 11) return isValidCPF(digits);
      if (digits.length === 14) return isValidCNPJ(digits);
      return false;
    }
    case RecipientPixKeyType.EMAIL:
      return validators[PixKeyErrorType.MAIL](trimmed);
    case RecipientPixKeyType.PHONE:
      return validators[PixKeyErrorType.PHONE](trimmed);
    case RecipientPixKeyType.RANDOM_KEY:
      return validators[PixKeyErrorType.RANDOM_KEY](trimmed);
    default:
      return false;
  }
}

/**
 * Cross-field validator for `pixKey`: reads sibling `pixKeyType` on the parent form.
 * Skips when empty (use `Validators.required` for presence).
 */
export function recipientBankAccountPixKeyCrossValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const parent = control.parent;
  if (!parent) return null;
  const pixKeyType = parent.get('pixKeyType')?.value as
    | RecipientPixKeyType
    | undefined;
  const value = control.value;
  if (value === '' || value === null || value === undefined) {
    return null;
  }
  if (pixKeyType === undefined || pixKeyType === null) {
    return null;
  }
  const ok = validateRecipientBankAccountPixKey(String(value), pixKeyType);
  if (ok) return null;
  if (pixKeyType === RecipientPixKeyType.RANDOM_KEY) {
    return {invalidUuid: true};
  }
  return {invalidPixKey: true};
}

export function validatePixKey(
  pixKey: string,
  pixKeyErrorType: PixKeyErrorType,
): boolean {
  const validator = validators[pixKeyErrorType];
  if (!validator) {
    console.warn(`Validator not found for type: ${pixKeyErrorType}`);
    return false;
  }
  return validator(pixKey);
}

export function pixKeyValidator(form: FormGroup) {
  return (control: AbstractControl) => {
    const pixKeyErrorType = form.get('pixKeyErrorType')?.value;
    const value = control.value;

    if (!pixKeyErrorType || !value) return null;

    const isValid = validatePixKey(value, pixKeyErrorType);

    return isValid ? null : {invalidPixKey: true};
  };
}

export function mapPixKeyErrorTypeToPixKeyType(
  type: PixKeyErrorType,
): PixKeyType {
  return type === PixKeyErrorType.CPF || type === PixKeyErrorType.CNPJ
    ? PixKeyType.DOCUMENT
    : (type as unknown as PixKeyType);
}
